import { Component, Input, Output, EventEmitter, SimpleChanges, OnChanges, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatChipsModule, MatChipSelectionChange } from '@angular/material/chips';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { isOrganisationTemplate, Template } from '../../../_interfaces/template';
import { SkeletonComponent } from '../skeleton/skeleton.component';
import { TooltipDirective } from '../tooltip/tooltip.directive';
import { TranslateModule } from '@ngx-translate/core';
import { DefaultTemplate } from '../../../_interfaces/user-settings.interface';
import { AuthService } from '../../../_services/auth.service';

type TemplateFilter = 'all' | 'default' | 'blueprints';

@Component({
  selector: 'app-template-selector',
  standalone: true,
  imports: [CommonModule, MatCheckboxModule, MatRadioModule, MatIconModule, MatChipsModule, MatButtonModule, SkeletonComponent, TooltipDirective, TranslateModule],
  templateUrl: './template-selector.component.html',
  styleUrl: './template-selector.component.scss',
  host: { class: 'w-full flex flex-col flex-1 min-h-0 h-full' },
})
export class TemplateSelector implements OnChanges {
  @Input() loading: boolean = false;
  @Input() templates: Template[] = [];
  @Input() selectedTemplates: DefaultTemplate[] = [];
  @Input() disabledTemplates: Template[] = [];
  @Input() approvedTemplates: Template[] = [];
  /** Changes reset internal selection so parent step switches do not leak state. */
  @Input() selectionKey = 'default';
  /** When true, list order stays as provided by the parent (no selection-based reordering). */
  @Input() preserveOrder = false;
  @Input() displayChips = true;
  /** When true, only one blueprint can be selected (radio UI); nothing is preselected on load. */
  @Input() singleSelection = false;
  @Output() selectionChange = new EventEmitter<Template[]>();

  private currentSelection: Map<string, Template> = new Map();
  private disabledKeys: Set<string> = new Set();
  private approvedKeys: Set<string> = new Set();
  private lastEmittedSelectionKey = '';
  defaultTemplates: Template[] = [];
  customTemplates: Template[] = [];
  allTemplates: Template[] = [];

  selectedTemplateFilter: TemplateFilter = 'all';
  readonly loadingSkeletonWidths = ['70%', '55%', '80%', '48%', '65%', '42%', '72%', '58%'];
  @ViewChild('templateListScroll') templateListScroll?: ElementRef<HTMLElement>;

  protected readonly isOrganisationTemplate = isOrganisationTemplate;

  constructor(private authService: AuthService) {}

  get showBlueprints(): boolean {
    return this.authService.isAiBlueprintsEnabled;
  }

  private templateKey(template: Pick<Template, 'id' | 'type'>): string {
    return `${String(template.id)}-${template.type}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabledTemplates']) {
      this.disabledKeys = new Set(
        (this.disabledTemplates || []).map((t) => this.templateKey(t))
      );
    }
    if (changes['approvedTemplates']) {
      this.approvedKeys = new Set(
        (this.approvedTemplates || []).map((t) => this.templateKey(t))
      );
    }
    if (changes['templates']) {
      this.defaultTemplates = this.templates.filter(t => isOrganisationTemplate(t.type));

      this.customTemplates = this.showBlueprints
        ? this.templates.filter(t => t.type === 'user')
        : [];

      this.allTemplates = [
        ...this.defaultTemplates,
        ...this.customTemplates,
      ]
      this.pruneHiddenSelection();
      this.checkSortTemplates();
    }
    if (this.singleSelection) {
      if (changes['selectionKey'] && !changes['selectionKey'].firstChange) {
        this.currentSelection.clear();
        this.checkSortTemplates();
        this.selectionEmmit();
      }
    } else if (changes['selectedTemplates'] || changes['selectionKey']) {
      this.applySelectedTemplates();
    }
  }

  private applySelectedTemplates(): void {
    this.currentSelection.clear();
    this.selectedTemplates.forEach((st) => {
      const template = this.templates.find((t) => this.templateKey(t) === this.templateKey(st));
      if (template && this.isSelectableType(template)) {
        this.currentSelection.set(this.templateKey(template), template);
      }
    });
    this.checkSortTemplates();
    this.selectionEmmit();
  }

  // Dynamic (user) blueprints must not be selectable when blueprints are disabled,
  // otherwise they would still be generated despite being hidden from the UI.
  private isSelectableType(template: Template): boolean {
    return this.showBlueprints || template.type !== 'user';
  }

  private pruneHiddenSelection(): void {
    if (this.showBlueprints) return;
    let removed = false;
    for (const [key, template] of this.currentSelection) {
      if (!this.isSelectableType(template)) {
        this.currentSelection.delete(key);
        removed = true;
      }
    }
    if (removed) {
      this.selectionEmmit();
    }
  }

  private checkSortTemplates(): void {
    if (this.preserveOrder) {
      return;
    }
    if (this.singleSelection || this.currentSelection.size > 0 || this.disabledKeys.size > 0) {
      this.defaultTemplates = this.sortTemplates(this.defaultTemplates);
      this.customTemplates = this.sortTemplates(this.customTemplates);
      this.allTemplates = this.sortTemplates(this.allTemplates);
    }
  }

  private sortTemplates(templates: Template[]): Template[] {
    return templates.sort((a, b) => {
      const aDisabled = this.isDisabled(a);
      const bDisabled = this.isDisabled(b);
      // Disabled (in-use) templates float to the top
      if (aDisabled && !bDisabled) return -1;
      if (!aDisabled && bDisabled) return 1;

      const aSelected = this.isSelected(a);
      const bSelected = this.isSelected(b);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return a.title.localeCompare(b.title);
    });
  }

  isSelected(template: Template): boolean {
    return this.currentSelection.has(this.templateKey(template));
  }

  isDisabled(template: Template): boolean {
    return this.disabledKeys.has(this.templateKey(template));
  }

  isApproved(template: Template): boolean {
    return this.approvedKeys.has(this.templateKey(template));
  }

  toggleSelection(template: Template): void {
    if (this.isDisabled(template)) return;
    if (!this.isSelectableType(template)) return;
    const key = this.templateKey(template);
    if (this.singleSelection) {
      if (this.currentSelection.has(key)) {
        return;
      }
      this.currentSelection.clear();
      this.currentSelection.set(key, template);
      this.selectionEmmit();
      return;
    }
    if (this.currentSelection.has(key)) {
      this.currentSelection.delete(key);
    } else {
      this.currentSelection.set(key, template);
    }
    this.selectionEmmit();
  }

  /** The organisation icon is informational; it must not toggle the row. */
  preventRowSelection(event: Event): void {
    if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
  }

  selectionEmmit(): void {
    const emittedKey = Array.from(this.currentSelection.keys())
      .sort((a, b) => a.localeCompare(b))
      .join('|');
    if (emittedKey === this.lastEmittedSelectionKey) {
      return;
    }
    this.lastEmittedSelectionKey = emittedKey;
    this.selectionChange.emit(Array.from(this.currentSelection.values()));
  }

  keepSelected(event: MatChipSelectionChange): void {
    if (event.isUserInput && !event.selected) {
      event.source.select();
    }
  }

  get filteredTemplates(): Template[] {
    switch (this.selectedTemplateFilter) {
      case 'default':
        return this.defaultTemplates;

      case 'blueprints':
        return this.showBlueprints ? this.customTemplates : [];

      case 'all':
        return this.allTemplates;
      default:
        return [];
    }
  }

  get filteredEmptyStateLabel(): string {
    return 'No blueprints available';
  }

  setTemplateFilter(filter: TemplateFilter): void {
    this.selectedTemplateFilter = filter;
    if (this.singleSelection && this.currentSelection.size > 0) {
      this.checkSortTemplates();
    }
    this.resetListScroll();
  }

  private resetListScroll(): void {
    const el = this.templateListScroll?.nativeElement;
    if (el) {
      el.scrollTop = 0;
    }
  }
}
